import { Button } from "@mui/material";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import { RATE_ICON_SRC } from "../constants";
import type { MatchProfileDetail } from "../../../services/matchesApi";
import type { DesignerBioImages, MatchRecord } from "../types";
import {
  formatDistance,
  getCompatibilityLabel,
  getCompatibilityRatingFromMatchScore,
  getCompatibilityStars,
  getCounterpartReaction,
  getCounterpartReactionBadge,
  getHeroImage,
  getLocationLabel,
  getMatchDisplayId,
  getPersonalityStyleResult,
  getPersonalityStyleColors,
  getProfileImage,
  getProfileSnapshot,
  getTopTags,
} from "../utils";
import { MatchScoreRing } from "./MatchScoreRing";
import { MatchStatusPill } from "./MatchStatusPill";

export const MatchDetailPanel = ({
  match,
  profile,
  profileImages,
  onViewProfile,
  onSelectPerfectMatch,
  canSelectPerfectMatch,
  selectingPerfectMatch,
  loadingProfile,
  canViewProfile = true,
  viewerAccountType,
}: {
  match: MatchRecord | null;
  profile: MatchProfileDetail | null;
  profileImages?: DesignerBioImages | null;
  onViewProfile: () => void;
  onSelectPerfectMatch?: () => void;
  canSelectPerfectMatch?: boolean;
  selectingPerfectMatch?: boolean;
  loadingProfile: boolean;
  canViewProfile?: boolean;
  viewerAccountType?: string | null;
}) => {
  if (!match) {
    return (
      <div className="matches-detail-panel content-block">
        <div className="matches-detail-empty">
          <p>Select a match to see details.</p>
        </div>
      </div>
    );
  }

  const snapshot = getProfileSnapshot(match);
  const topTags = getTopTags(match, profile);
  const certifications =
    profile?.certifications ||
    ((snapshot?.certifications as string[] | undefined) ?? []);
  const education =
    profile?.education || ((snapshot?.education as string[] | undefined) ?? []);
  const personalityStyleResult = getPersonalityStyleResult(match, profile);
  const personalityColors = getPersonalityStyleColors(match, profile);
  const [interactionColor, primaryColor, secondaryColor] = personalityColors ?? [
    "#6a53a5",
    "#1c3766",
    "#d24b1f",
  ];
  const compatibilityRating =
    getCompatibilityRatingFromMatchScore(match.matchScore) ??
    profile?.compatibilityRating ??
    null;
  const stars = getCompatibilityStars(compatibilityRating);
  const compatibilityLabel = getCompatibilityLabel(compatibilityRating);
  const isOnline = profile?.isOnline ?? match.isOnline ?? false;
  const locationLabel = profile?.location || getLocationLabel(match);
  const counterpartReactionBadge = getCounterpartReactionBadge(
    getCounterpartReaction(match)
  );
  const showMatchMade = Boolean(match.isMatchMade);
  const showSelectPerfectMatch =
    Boolean(canSelectPerfectMatch) && !showMatchMade;
  const profileImage = getProfileImage(match, profileImages, profile);
  const displayMatchId = getMatchDisplayId(match.matchId, viewerAccountType);
  const heroImage = getHeroImage(
    match,
    profileImages,
    profile,
    profileImage,
    viewerAccountType
  );
  const credentials = [
    ...certifications.map((item) => ({ type: "cert", text: item })),
    ...education.map((item) => ({ type: "edu", text: item })),
  ];
  const isDesignerViewer = (viewerAccountType ?? "").toLowerCase() === "designer";

  return (
    <div className="matches-detail-panel content-block">
      <div className="matches-detail-header">
        <div>
          <h2 className="matches-detail-title">{displayMatchId}</h2>
        </div>
        <div className="matches-detail-status">
          <span className={`status-dot ${isOnline ? "online" : "offline"}`} />
          <span>{isOnline ? "Online" : "Offline"}</span>
        </div>
      </div>

      <div className="matches-detail-rating-block">
        <div className="matches-detail-rating-row">
          <div className="matches-detail-rating">
            <div className="matches-detail-stars">
              {stars.map((state, index) => (
                <img
                  key={`${state}-${index}`}
                  src={RATE_ICON_SRC[state]}
                  alt={`${state} star`}
                  className={`rating-star is-${state}`}
                  loading="lazy"
                />
              ))}
            </div>
            <span className="matches-detail-rating-text">
              {compatibilityRating
                ? `(${compatibilityRating.toFixed(1)})${
                    compatibilityLabel ? ` ${compatibilityLabel}` : ""
                  }`
                : ""}
            </span>
          </div>
          <Button
            variant="text"
            className="matches-view-profile matches-view-profile-inline"
            onClick={onViewProfile}
            disabled={loadingProfile || !canViewProfile}
            sx={{ color: "#4F46E5" }}
          >
            View Full Profile
          </Button>
        </div>
        <div className="matches-detail-rating-label">Compatibility Rating</div>
      </div>

      <div className="matches-detail-hero">
        {heroImage ? (
          <img src={heroImage} alt={`${displayMatchId} cover`} loading="lazy" />
        ) : (
          <div className="matches-detail-hero-placeholder" />
        )}
      </div>

      <div className="matches-detail-meta-stack">
        <div className="matches-detail-meta">
          <span>
            <PlaceOutlinedIcon />
            {locationLabel}
          </span>
          <span>
            <RouteOutlinedIcon />
            {formatDistance(match.distanceMiles ?? profile?.distanceMiles ?? null)}
          </span>
        </div>
        {showMatchMade ? (
          <Button
            variant="contained"
            className="matches-select-match matches-select-match--inline matches-select-match--made"
            disabled
          >
            Match Made
          </Button>
        ) : showSelectPerfectMatch ? (
          <Button
            variant="outlined"
            className="matches-select-match matches-select-match--inline"
            onClick={onSelectPerfectMatch}
            disabled={Boolean(selectingPerfectMatch) || loadingProfile}
          >
            {selectingPerfectMatch ? "Selecting..." : "Select as Perfect Match"}
          </Button>
        ) : (
          counterpartReactionBadge && (
            <MatchStatusPill
              tone={counterpartReactionBadge.tone}
              label={counterpartReactionBadge.label}
              className="matches-detail-counterpart-reaction"
            />
          )
        )}
      </div>

      <div className="matches-detail-top">
        <div>
          <h3>Top Matching Areas</h3>
          <div className="matches-chip-row">
            {topTags.length ? (
              topTags.slice(0, 3).map((tag) => (
                <span key={tag} className="matches-chip">
                  {tag}
                </span>
              ))
            ) : (
              <span className="matches-chip matches-chip-muted">No tags yet</span>
            )}
          </div>
        </div>
        <div className="matches-detail-score">
          <MatchScoreRing value={match.matchScore} size="large" />
          <span>Smart-Guild Matching Overall Score</span>
        </div>
      </div>

      {isDesignerViewer ? (
        <div className="matches-detail-personality">
          <h3>{personalityStyleResult || "Personality Style Result"}</h3>
          {personalityStyleResult ? (
            <div className="personality-grid">
              <div className="personality-legend">
                <div>
                  <span
                    className="legend-dot"
                    style={{ backgroundColor: interactionColor }}
                  />
                  <span>Interaction Style</span>
                </div>
                <div>
                  <span
                    className="legend-dot"
                    style={{ backgroundColor: primaryColor }}
                  />
                  <span>First Color</span>
                </div>
                <div>
                  <span
                    className="legend-dot"
                    style={{ backgroundColor: secondaryColor }}
                  />
                  <span>Second Color</span>
                </div>
              </div>
              <div
                className="personality-pyramid"
                aria-hidden="true"
                style={{
                  background: `linear-gradient(to top, ${secondaryColor} 0 33%, ${primaryColor} 33% 66%, ${interactionColor} 66% 100%)`,
                }}
              />
            </div>
          ) : (
            <p>Personality style not provided.</p>
          )}
        </div>
      ) : (
        <div className="matches-detail-credentials">
          <h4>Certifications & Education</h4>
          {credentials.length ? (
            <ul className="matches-detail-credential-list">
              {credentials.map((item) => (
                <li key={`${item.type}-${item.text}`}>
                  {item.type === "edu" ? (
                    <SchoolOutlinedIcon />
                  ) : (
                    <WorkspacePremiumOutlinedIcon />
                  )}
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p>No certifications or education listed.</p>
          )}
        </div>
      )}

      <div className="matches-detail-cta">
        <Button
          variant="text"
          className="matches-view-profile"
          onClick={onViewProfile}
          disabled={loadingProfile || !canViewProfile}
          sx={{ color: "#4F46E5" }}
        >
          View Full Profile
        </Button>
      </div>
    </div>
  );
};
